import React, { useState, useEffect, useRef } from 'react';
import { getDevices, getUsers, deviceOwners, setDeviceOwners, deviceMembership, setDeviceMembership, membershipStatus, deleteDevice, updateDevice } from '../traccar';
import { getCustomers } from '../customerStore';

// Membership status → pill colors + label. This is what tells you at a glance
// whether a device is live, lapsed, or manually stopped.
const MSTATUS = {
  active: { bg: '#f0fdf4', fg: '#15803d', bd: '#bbf7d0', label: 'Active' },
  expired: { bg: '#fef2f2', fg: '#b91c1c', bd: '#fecaca', label: 'Expired' },
  suspended: { bg: '#fef3c7', fg: '#b45309', bd: '#fde68a', label: 'Stopped' },
  unassigned: { bg: '#f1f5f9', fg: '#64748b', bd: '#e2e8f0', label: 'Unassigned' },
};
const prettyDate = (ymd) => { if (!ymd) return '—'; const [y, m, d] = String(ymd).split('-'); return `${m}/${d}/${y}`; };

// ---------------------------------------------------------------
// Admin: Devices — a device-first view for ASSIGNING a tracker to one or more
// customer accounts. Search a device by the last 4 of its IMEI, then add or
// remove any accounts. A device can belong to several accounts at once (each
// assigned account sees it in their app).
// ---------------------------------------------------------------

const card = { background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', border: '1px solid #e5e7eb' };
const inp = { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '9px', fontSize: '14px', width: '100%', boxSizing: 'border-box', background: '#fff' };
const chip = (bg, fg, bd) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', background: bg, color: fg, border: `1px solid ${bd}`, borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 700 });

export default function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null); // device whose assign panel is open
  const [pick, setPick] = useState(''); // account search inside the open device
  const [busyId, setBusyId] = useState(null);
  const [expDraft, setExpDraft] = useState({}); // device id -> edited 'YYYY-MM-DD' expiration

  useEffect(() => {
    getDevices().then(setDevices).catch(() => {});
    setCustomers(getCustomers());
    Promise.resolve(getUsers()).then((us) => setUsers(us || [])).catch(() => setUsers([]));
  }, []);

  // Assignable accounts = EVERY login (Traccar user), enriched from the CRM when
  // there's a matching customer record. This is why your own account shows even
  // if it isn't a CRM customer.
  const accounts = (() => {
    const byUid = {}, byEmail = {};
    customers.forEach((c) => { if (c.traccarUserId) byUid[String(c.traccarUserId)] = c; if (c.email) byEmail[c.email.toLowerCase()] = c; });
    return (users || []).map((u) => {
      const c = byUid[String(u.id)] || byEmail[(u.email || '').toLowerCase()] || null;
      return {
        userId: u.id,
        name: (c && c.name) || u.name || u.email || `User ${u.id}`,
        account: (c && c.account) || (u.attributes && u.attributes.account) || '',
        customerId: (c && c.id) || '',
        email: u.email || (c && c.email) || '',
      };
    });
  })();

  const shown = (() => {
    const s = q.trim().toLowerCase();
    if (!s) return devices;
    return devices.filter((d) => {
      const imei = String(d.uniqueId || '');
      const name = String(d.name || '').toLowerCase();
      const dn = String((d.attributes || {}).displayName || '').toLowerCase();
      return imei.slice(-4) === s || imei.includes(s) || name.includes(s) || dn.includes(s);
    });
  })();

  const ownersOf = (d) => deviceOwners(d);
  const isAssigned = (d, acc) => ownersOf(d).some((o) => String(o.userId) === String(acc.userId));

  // Match a saved owner entry to a real account by any signal it carries.
  const resolveAccount = (o) => (o ? accounts.find((a) => (
    (o.userId && String(a.userId) === String(o.userId))
    || (o.customerId && a.customerId && String(a.customerId) === String(o.customerId))
    || (o.account && a.account && String(a.account) === String(o.account))
    || (o.name && a.name && a.name.toLowerCase() === String(o.name).toLowerCase())
  )) : null) || null;
  // Show the customer's NAME on the chip, not a bare id/account number.
  const ownerLabel = (o) => {
    const a = resolveAccount(o);
    return (a && a.name) || o.name || (o.account ? `#${o.account}` : (o.userId ? `Account ${o.userId}` : 'Account'));
  };

  // People ADDED to the device with a share code (family / authorized viewers) —
  // stored as approved memberLinks that are NOT owner (admin) links. They can see
  // the car but are not owners. memberId is "u<userId>".
  const sharedUsersOf = (d) => {
    // Anyone who is already an OWNER is not an "added user" — exclude them so an
    // owner who also has an old share link doesn't show in both rows.
    const ownerUids = new Set(deviceOwners(d).map((o) => String(o.userId)).filter(Boolean));
    return (((d.attributes || {}).memberLinks) || [])
      .filter((m) => m && m.status === 'approved' && m.by !== 'admin')
      .filter((m) => !ownerUids.has(String(m.memberId || '').replace(/^u/, '')));
  };
  const sharedLabel = (m) => {
    if (m.name) return m.name;
    const uid = String(m.memberId || '').replace(/^u/, '');
    const a = accounts.find((x) => String(x.userId) === uid);
    return (a && a.name) || `User ${uid}`;
  };
  const removeSharedUser = async (d, memberId) => {
    setBusyId(d.id);
    try {
      const links = (((d.attributes || {}).memberLinks) || []).filter((m) => m.memberId !== memberId);
      const updated = { ...d, attributes: { ...(d.attributes || {}), memberLinks: links } };
      await updateDevice(updated);
      setDevices((list) => list.map((x) => (x.id === d.id ? updated : x)));
    } catch { alert('Could not remove the added user.'); }
    setBusyId(null);
  };

  // One-time AUTO-REPAIR: an owner saved with only a customer id / account / name
  // (no stable Traccar user id) can't be matched to the person's phone for push.
  // On load, backfill the missing user id from the matching account and persist,
  // so every assignment self-heals — no more re-adding by hand.
  const repairedRef = useRef(false);
  useEffect(() => {
    if (repairedRef.current || !devices.length || !accounts.length) return;
    repairedRef.current = true;
    (async () => {
      for (const d of devices) {
        const owners = deviceOwners(d);
        if (!owners.length) continue;
        let changed = false;
        const fixed = owners.map((o) => {
          if (o.userId) return o; // already stable
          const a = resolveAccount(o);
          if (a && a.userId) {
            changed = true;
            return { userId: a.userId, customerId: o.customerId || a.customerId || '', account: o.account || a.account || '', name: o.name || a.name || '' };
          }
          return o;
        });
        // Heal a DRIFTED primary: if the device's top-level ownerUserId/customerId/
        // account no longer match its first owner (e.g. it was reassigned but the
        // old primary lingered), re-save so the primary mirrors owners[0]. This is
        // what makes a car count for the RIGHT customer everywhere — Customers
        // tab, Devices chips, the app, and push all agree.
        const a0 = fixed[0] || {};
        const at = d.attributes || {};
        const primaryDrifted = fixed.length && (
          String(at.ownerUserId || '') !== String(a0.userId || '')
          || String(at.customerId || '') !== String(a0.customerId || '')
          || String(at.account || '') !== String(a0.account || '')
        );
        if (changed || primaryDrifted) {
          try { const updated = await setDeviceOwners(d, fixed); setDevices((list) => list.map((x) => (x.id === d.id ? updated : x))); }
          catch { /* leave as-is; will retry next load */ }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, accounts]);

  const save = async (d, owners) => {
    setBusyId(d.id);
    try {
      const updated = await setDeviceOwners(d, owners);
      setDevices((list) => list.map((x) => (x.id === d.id ? updated : x)));
    } catch { alert('Could not update the assignment.'); }
    finally { setBusyId(null); }
  };
  const addAccount = (d, acc) => {
    if (isAssigned(d, acc)) return;
    save(d, [...ownersOf(d), { userId: acc.userId, customerId: acc.customerId, account: acc.account, name: acc.name }]);
  };
  const removeAccount = (d, userId) => save(d, ownersOf(d).filter((o) => String(o.userId) !== String(userId)));

  // ---- Membership / expiration ----
  const saveMembership = async (d, patch) => {
    setBusyId(d.id);
    try {
      const updated = await setDeviceMembership(d, patch);
      setDevices((list) => list.map((x) => (x.id === d.id ? updated : x)));
    } catch { alert('Could not update the membership.'); }
    finally { setBusyId(null); }
  };
  const toggleStop = (d) => saveMembership(d, { suspended: !deviceMembership(d).suspended });

  // Permanently erase a device from Traccar so its IMEI is free to re-add.
  const eraseDevice = async (d) => {
    const label = `${d.name}${(d.attributes || {}).displayName ? ` “${d.attributes.displayName}”` : ''}`;
    if (!window.confirm(`Erase ${label} (IMEI ${d.uniqueId})?\n\nThis permanently removes the device and all its assignments from the system. You can add the same tracker again afterward on Device Intake.\n\nThis cannot be undone.`)) return;
    setBusyId(d.id);
    try {
      await deleteDevice(d.id);
      setDevices((list) => list.filter((x) => x.id !== d.id));
      if (openId === d.id) setOpenId(null);
    } catch { alert('Could not erase the device. It may still be linked — try again in a moment.'); }
    finally { setBusyId(null); }
  };
  const saveExpiration = (d) => {
    const val = expDraft[d.id];
    if (!val) return;
    saveMembership(d, { expiresAt: val, suspended: false }); // saving a fresh date also un-stops it
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>📟 Devices</h1>
        <p>Search a tracker by the last 4 of its IMEI, then assign it to one or more accounts. A device can be assigned to as many accounts as you like — each one sees it in their app.</p>
      </div>

      <div style={{ ...card, marginBottom: '14px' }}>
        <input autoFocus style={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Search by last 4 of IMEI (e.g. 6474), full IMEI, or name" />
        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{shown.length} of {devices.length} devices · {accounts.length} assignable account(s)</div>
      </div>

      {shown.length === 0 && <div style={{ ...card, color: '#94a3b8', textAlign: 'center', padding: '40px' }}>No devices match.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {shown.map((d) => {
          const owners = ownersOf(d);
          const open = openId === d.id;
          const imei = String(d.uniqueId || '');
          const mem = deviceMembership(d);
          const mstat = membershipStatus(d);
          const ms = MSTATUS[mstat] || MSTATUS.unassigned;
          const options = accounts.filter((a) => {
            if (isAssigned(d, a)) return false;
            const s = pick.trim().toLowerCase();
            // search by first name, last name, account number, or email
            return !s || `${a.name} ${a.account} ${a.email || ''}`.toLowerCase().includes(s);
          });
          return (
            <div key={d.id} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: d.status === 'online' ? '#22c55e' : '#cbd5e1' }} />
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>{d.name}{(d.attributes || {}).displayName ? ` · “${d.attributes.displayName}”` : ''}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>IMEI {imei} <span style={{ background: '#eef2ff', color: '#4338ca', borderRadius: '6px', padding: '1px 6px', fontWeight: 700 }}>··{imei.slice(-4)}</span></div>
                </div>
                {owners.length > 0 && (
                  <span style={chip(ms.bg, ms.fg, ms.bd)} title={mstat === 'expired' ? `Expired ${prettyDate(mem.expiresAt)} — tracking stopped` : mstat === 'suspended' ? 'Manually stopped — tracking off' : mstat === 'active' ? `Renews/expires ${prettyDate(mem.expiresAt)}` : ''}>
                    {mstat === 'active' ? '🟢' : mstat === 'expired' ? '⛔' : mstat === 'suspended' ? '⏸' : '•'} {ms.label}
                  </span>
                )}
                <button onClick={() => { setOpenId(open ? null : d.id); setPick(''); setExpDraft((m) => ({ ...m, [d.id]: deviceMembership(d).expiresAt || '' })); }} style={{ background: open ? '#eef2ff' : 'linear-gradient(135deg,#0066ff,#0047d0)', color: open ? '#1d4ed8' : 'white', border: 'none', borderRadius: '9px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                  {open ? 'Done' : (owners.length ? `Assigned to ${owners.length}` : 'Assign')}
                </button>
                <button onClick={() => eraseDevice(d)} disabled={busyId === d.id} title="Erase device — removes it completely and frees the IMEI so you can add it again" style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '9px', padding: '8px 11px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>🗑</button>
              </div>

              {/* current assignments (OWNERS) */}
              {owners.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                  {owners.map((o, i) => (
                    <span key={o.userId || i} style={chip('#f0fdf4', '#15803d', '#bbf7d0')} title={i === 0 ? 'Primary owner' : 'Owner'}>
                      {i === 0 ? '👑 ' : ''}{ownerLabel(o)}
                      <button onClick={() => removeAccount(d, o.userId)} disabled={busyId === d.id} title="Remove" style={{ background: 'transparent', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}

              {/* ADDED USERS (via a share code) — can view the car, not owners */}
              {sharedUsersOf(d).length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: owners.length ? '6px' : '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>👁 Added users:</span>
                  {sharedUsersOf(d).map((m) => (
                    <span key={m.memberId} style={chip('#eff6ff', '#1d4ed8', '#bfdbfe')} title="Added with a share code — can view this car, but is not the owner">
                      {sharedLabel(m)}
                      <button onClick={() => removeSharedUser(d, m.memberId)} disabled={busyId === d.id} title="Remove this added user" style={{ background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}

              {/* assign panel */}
              {open && (
                <div style={{ marginTop: '12px', borderTop: '1px solid #eef2f7', paddingTop: '12px' }}>
                  {/* Membership / expiration — only meaningful once the device is assigned */}
                  {owners.length > 0 && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>💳 Membership</span>
                        <span style={chip(ms.bg, ms.fg, ms.bd)}>{ms.label}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end' }}>
                        <div>
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '3px' }}>Activated</div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{prettyDate(mem.activatedAt)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, marginBottom: '3px' }}>Stops on (expiration)</div>
                          <input type="date" value={expDraft[d.id] ?? (mem.expiresAt || '')} onChange={(e) => setExpDraft((m) => ({ ...m, [d.id]: e.target.value }))} style={{ ...inp, width: '160px', padding: '7px 9px' }} />
                        </div>
                        <button onClick={() => saveExpiration(d)} disabled={busyId === d.id || !(expDraft[d.id] ?? mem.expiresAt) || (expDraft[d.id] ?? mem.expiresAt) === mem.expiresAt}
                          style={{ background: '#0066ff', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: (busyId === d.id || !(expDraft[d.id] ?? mem.expiresAt) || (expDraft[d.id] ?? mem.expiresAt) === mem.expiresAt) ? 0.5 : 1 }}>
                          Save date
                        </button>
                        <button onClick={() => toggleStop(d)} disabled={busyId === d.id}
                          style={{ background: mem.suspended ? '#16a34a' : '#fff', color: mem.suspended ? '#fff' : '#b91c1c', border: `1px solid ${mem.suspended ? '#16a34a' : '#fecaca'}`, borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                          {mem.suspended ? '▶ Resume tracking' : '⏸ Stop tracking now'}
                        </button>
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '9px', lineHeight: 1.5 }}>
                        {mstat === 'expired' && <span style={{ color: '#b91c1c', fontWeight: 700 }}>Membership lapsed — this customer can no longer track or get reports. Set a new date to reactivate. </span>}
                        {mstat === 'suspended' && <span style={{ color: '#b45309', fontWeight: 700 }}>Manually stopped — tracking &amp; reporting are off for the customer. </span>}
                        Tracking and reporting stop on the expiration date until the customer renews. Editing the date reactivates the device.
                      </div>
                    </div>
                  )}
                  <input style={{ ...inp, marginBottom: '8px' }} value={pick} onChange={(e) => setPick(e.target.value)} placeholder="Search accounts to add…" />
                  <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {options.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>No matching accounts{accounts.length === 0 ? ' — customers need a login first (create it on the Customers page).' : ''}.</div>}
                    {options.slice(0, 40).map((a) => (
                      <button key={a.userId} onClick={() => addAccount(d, a)} disabled={busyId === d.id}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 11px', cursor: 'pointer' }}>
                        <span><span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{a.name}</span> <span style={{ fontSize: '11px', color: '#94a3b8' }}>#{a.account}</span></span>
                        <span style={{ color: '#0066ff', fontWeight: 800, fontSize: '13px' }}>＋ Add</span>
                      </button>
                    ))}
                  </div>
                  {owners.length > 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>👑 = primary owner (first assigned). Remove all to unassign the device.</div>}

                  {/* Danger zone — erase the device so its IMEI is free to re-add */}
                  <div style={{ marginTop: '14px', borderTop: '1px dashed #fecaca', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: '#94a3b8', maxWidth: '360px' }}>Erase this device to remove it completely and free its IMEI, so you can add the same tracker again on Device Intake.</span>
                    <button onClick={() => eraseDevice(d)} disabled={busyId === d.id}
                      style={{ background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                      🗑 Erase device
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
